import React, { useContext, useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import { getHistoryByStore, getHistoryDates } from "../../../api/historyApi";
import { LanguageContext } from "../../../components/LanguageContext";

const TrendCell = React.memo(({ value, trend }) => {
    if (value == null || isNaN(parseFloat(value))) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }

    const num = typeof value === "number" ? value : parseFloat(value);
    const TrendIcon =
        trend === "up"
            ? TrendingUpRoundedIcon
            : trend === "down"
            ? TrendingDownRoundedIcon
            : TrendingFlatRoundedIcon;
    const tone =
        trend === "up" ? "success" : trend === "down" ? "error" : "neutral";

    return (
        <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={(theme) => ({
                px: 1,
                py: 0.4,
                borderRadius: 1.5,
                bgcolor:
                    tone === "neutral"
                        ? "transparent"
                        : alpha(theme.palette[tone].main, 0.1),
            })}
        >
            <TrendIcon
                sx={{
                    fontSize: "1rem",
                    color: tone === "neutral" ? "text.disabled" : `${tone}.main`,
                }}
            />
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                }}
            >
                {num.toFixed(2)}
            </Typography>
        </Stack>
    );
});

const HistoryByStorePage = () => {
    const { translations } = useContext(LanguageContext) || {};
    const { storeCode } = useParams();

    const { data: data1, isLoading: isLoading1 } = useQuery({
        queryKey: ["history", storeCode],
        queryFn: () => getHistoryByStore(storeCode),
        staleTime: 5 * 60 * 1000,
    });

    const { data: data2, isLoading: isLoading2 } = useQuery({
        queryKey: ["dates"],
        queryFn: () => getHistoryDates(),
        staleTime: 5 * 60 * 1000,
    });

    const dates = useMemo(() => {
        if (!data2?.data) return [];
        return data2.data.map((item) => (Array.isArray(item) ? item[0] : item));
    }, [data2]);

    const rows = useMemo(() => {
        if (isLoading1 || !data1?.data || !dates.length) return [];

        return data1.data.map((d) => {
            const row = {
                id: d.code,
                store_code: d.store_code,
                code: d.code,
                name: d.name,
            };

            const arr = JSON.parse(d.history);
            const dateMap = new Map();
            arr.forEach((item) => {
                const cur = dateMap.get(item.date) || 0;
                dateMap.set(item.date, cur + parseFloat(item.value));
            });

            dates.forEach((date, index) => {
                const value = dateMap.get(date) || 0;
                row[date] = value;
                if (index > 0) {
                    const prev = row[dates[index - 1]] || 0;
                    row[`${date}_trend`] =
                        value > prev ? "up" : value < prev ? "down" : null;
                } else {
                    row[`${date}_trend`] = null;
                }
            });

            return row;
        });
    }, [data1, isLoading1, dates]);

    const columns = useMemo(
        () => [
            {
                field: "store_code",
                headerName:
                    translations?.adminHistoryPageStoreCode || "Šifra prodavnice",
                width: 150,
                align: "center",
                headerAlign: "center",
                pinnable: true,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    />
                ),
            },
            {
                field: "code",
                headerName:
                    translations?.adminHistoryPageProductCode || "Šifra proizvoda",
                width: 160,
                align: "center",
                headerAlign: "center",
                pinnable: true,
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={(theme) => ({
                            fontFamily: "monospace",
                            fontWeight: 600,
                            bgcolor:
                                theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(15,23,42,0.04)",
                        })}
                    />
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminAnalysisProductName || "Naziv proizvoda",
                minWidth: 200,
                pinnable: true,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={params.value}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            ...dates.map((d) => ({
                field: d,
                headerName: d,
                width: 130,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <TrendCell
                        value={params.value}
                        trend={params.row[`${d}_trend`]}
                    />
                ),
            })),
        ],
        [dates, translations]
    );

    return (
        <Box>
            <PageHeader
                icon={<HistoryOutlinedIcon />}
                eyebrow={translations?.historyByStore || "History"}
                title={translations?.historyByStore || "Istorija po prodavnici"}
                description={`${translations?.historySubtitle || "Pregled istorije prodaje"} ${storeCode}`}
            />

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading1 || isLoading2}
                    rowHeight={56}
                    emptyIcon={<HistoryOutlinedIcon />}
                    emptyTitle={
                        translations?.noHistoryTitle || "Nema podataka o istoriji"
                    }
                    emptyDescription={
                        translations?.noHistoryDescription ||
                        "Sinhronizujte istoriju prodaje iz Navigatora."
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default HistoryByStorePage;